"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "next/navigation";
import {
  ChevronDown, Check, Settings, User, Briefcase, Users, Globe,
  Building2, Wrench, LogOut, Code2, GripVertical,
} from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { getTranslations } from "@/lib/i18n";
import type { User as SupabaseUser } from "@supabase/supabase-js";

// ─── Workspace definitions ──────────────────────────────

interface WorkspaceItem {
  id: string;
  icon: React.ElementType;
  gradient: string;
  name: string;
  desc: string;
  href?: string;
  external?: boolean;
  activePathPrefixes?: string[];
  badge?: { label: string; class: string };
  disabled?: boolean;
  notif?: boolean;
}

interface WorkspaceSection {
  label: string;
  items: WorkspaceItem[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getWorkspaceSections(lang: "he" | "en" | "ru", t: any): WorkspaceSection[] {
  const ws = t.workspaceSwitcher;
  return [
    {
      label: ws.sections.personal,
      items: [
        {
          id: "personal",
          icon: User,
          gradient: "from-blue-500 to-cyan-500",
          name: ws.workspaces.personal.name,
          desc: ws.workspaces.personal.desc,
        },
      ],
    },
    {
      label: ws.sections.business,
      items: [
        {
          id: "gam-business",
          icon: Briefcase,
          gradient: "from-teal-500 to-teal-500",
          name: ws.workspaces.gamBusiness.name,
          desc: ws.workspaces.gamBusiness.desc,
          badge: { label: ws.badges.owner, class: "bg-teal-500/15 text-teal-400" },
        },
        {
          id: "gallor",
          icon: Briefcase,
          gradient: "from-teal-500 to-teal-400",
          name: ws.workspaces.gallor.name,
          desc: ws.workspaces.gallor.desc,
          badge: { label: ws.badges.owner, class: "bg-teal-500/15 text-teal-400" },
        },
      ],
    },
    {
      label: ws.sections.groups,
      items: [
        {
          id: "groups",
          icon: Users,
          gradient: "from-amber-500 to-red-500",
          name: ws.workspaces.groups.name,
          desc: ws.workspaces.groups.desc,
          disabled: true,
          badge: { label: ws.badges.soon, class: "bg-slate-500/20 text-slate-500 italic" },
        },
      ],
    },
    {
      label: ws.sections.platform,
      items: [
        {
          id: "vbrain-office",
          icon: Building2,
          gradient: "from-emerald-500 to-emerald-600",
          name: ws.workspaces.vbrainOffice.name,
          desc: ws.workspaces.vbrainOffice.desc,
          badge: { label: ws.badges.admin, class: "bg-red-500/15 text-red-400" },
        },
        {
          id: "vbrain-crm",
          icon: Users,
          gradient: "from-teal-500 to-cyan-500",
          name: ws.workspaces.vbrainCrm.name,
          desc: ws.workspaces.vbrainCrm.desc,
          href: "/dashboard/crm",
          activePathPrefixes: ["/dashboard/crm"],
        },
      ],
    },
    {
      label: "Super Admin",
      items: [
        {
          id: "command-center",
          icon: Settings,
          gradient: "from-pink-500 to-teal-500",
          name: ws.workspaces.commandCenter.name,
          desc: ws.workspaces.commandCenter.desc,
          href: "/dashboard",
        },
        {
          id: "development",
          icon: Code2,
          gradient: "from-[var(--cc-accent-500)] to-[var(--cc-accent-600)]",
          name: ws.workspaces.development.name,
          desc: ws.workspaces.development.desc,
          href: "https://vbrain.win/dashboard",
          external: true,
        },
        {
          id: "gam-tools",
          icon: Wrench,
          gradient: "from-orange-500 to-amber-500",
          name: ws.workspaces.gamTools.name,
          desc: ws.workspaces.gamTools.desc,
          notif: true,
        },
        {
          id: "gam-site",
          icon: Globe,
          gradient: "from-cyan-500 to-blue-500",
          name: ws.workspaces.gamSite.name,
          desc: ws.workspaces.gamSite.desc,
          href: "/dashboard/site-control",
          activePathPrefixes: ["/dashboard/site-control"],
        },
      ],
    },
  ];
}

// ─── Component ──────────────────────────────────────────

interface WorkspaceSwitcherProps {
  user: SupabaseUser;
  isCollapsed: boolean;
  onRight: boolean;
  expandedWidth: number;
  navTop: number;
}

export function WorkspaceSwitcher({ user, isCollapsed, onRight, expandedWidth: _expandedWidth, navTop: _navTop }: WorkspaceSwitcherProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { language } = useSettings();
  const { signOut } = useAuth();
  const t = getTranslations(language);
  const isRtl = language === "he";
  const lang = language === "he" ? "he" : language === "ru" ? "ru" : "en";

  const [open, setOpen] = useState(false);
  const [activeWs, setActiveWs] = useState("command-center");
  const [sectionOrder, setSectionOrder] = useState<Record<string, string[]>>({});
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 280 });

  const sections = getWorkspaceSections(lang as "he" | "en" | "ru", t);
  const orderedSections = sections.map((section) => ({
    ...section,
    items: sectionOrder[section.label]
      ? [...section.items].sort((a, b) => {
          const order = sectionOrder[section.label];
          return (order.indexOf(a.id) < 0 ? order.length : order.indexOf(a.id)) -
            (order.indexOf(b.id) < 0 ? order.length : order.indexOf(b.id));
        })
      : section.items,
  }));
  const allItems = orderedSections.flatMap((section) => section.items);
  const routeItem = allItems.find((item) =>
    item.activePathPrefixes?.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    ),
  );
  const activeItem = routeItem ?? allItems.find((item) => item.id === activeWs);
  const activeItemId = activeItem?.id ?? activeWs;

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem("gam-workspace-switcher-order");
      if (saved) setSectionOrder(JSON.parse(saved) as Record<string, string[]>);
    } catch {
      // A stale or unavailable preference should never block the switcher.
    }
  }, []);

  const persistSectionOrder = (next: Record<string, string[]>) => {
    setSectionOrder(next);
    try {
      window.localStorage.setItem("gam-workspace-switcher-order", JSON.stringify(next));
    } catch {
      // Preferences are best effort; the current order still applies in memory.
    }
  };

  const moveItem = (sectionLabel: string, targetId: string) => {
    if (!draggedItem || draggedItem === targetId) return;
    const items = orderedSections.find((section) => section.label === sectionLabel)?.items ?? [];
    const from = items.findIndex((item) => item.id === draggedItem);
    const to = items.findIndex((item) => item.id === targetId);
    if (from < 0 || to < 0) return;
    const ids = items.map((item) => item.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    persistSectionOrder({ ...sectionOrder, [sectionLabel]: ids });
    setDraggedItem(null);
  };

  // Compute dropdown position from trigger rect
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropPos({
      top: rect.bottom + 6,
      left: isCollapsed ? (onRight ? rect.right - 280 : rect.left) : rect.left,
      width: isCollapsed ? 300 : Math.max(rect.width, 320),
    });
  }, [isCollapsed, onRight]);

  useEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const userInitial = (user.email?.[0] || "?").toUpperCase();
  const userName = user.user_metadata?.full_name || user.email?.split("@")[0] || "User";

  // ─── Collapsed: compact avatar trigger ───
  if (isCollapsed) {
    return (
      <div className="shrink-0 border-b border-slate-700/50 py-2 flex justify-center" ref={triggerRef}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="group relative flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--cc-accent-500)]/10 border border-[var(--cc-accent-500)]/20 hover:bg-[var(--cc-accent-500)]/[0.18] hover:border-[var(--cc-accent-500)]/35 transition-all cursor-pointer"
        >
          <span className="text-sm font-bold text-[var(--cc-accent-300)]">{userInitial}</span>
          <span className="absolute -bottom-0.5 -end-0.5 h-2.5 w-2.5 rounded-full border-2 border-slate-900 bg-emerald-500" />
        </button>

        {open && renderDropdown()}
      </div>
    );
  }

  // ─── Expanded: workspace trigger ───
  return (
    <div className="shrink-0 border-b border-slate-700/50" ref={triggerRef}>
      {/* Workspace trigger */}
      <div className="px-2 py-2 relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 bg-[var(--cc-accent-500)]/[0.08] border border-[var(--cc-accent-500)]/15 hover:bg-[var(--cc-accent-500)]/[0.14] hover:border-[var(--cc-accent-500)]/25 transition-all cursor-pointer ${isRtl ? "flex-row-reverse" : ""}`}
        >
          {/* Workspace icon */}
          {activeItem && (
            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${activeItem.gradient}`}>
              <activeItem.icon className="h-3.5 w-3.5 text-white" />
            </div>
          )}

          {/* Workspace name */}
          <div className={`flex-1 min-w-0 ${isRtl ? "text-end" : "text-start"}`}>
            <div className="truncate text-[11px] font-medium text-slate-300">
              {activeItem ? activeItem.name.replace("vBrain — ", "").replace("Gallor ", "") : "Super Admin"}
            </div>
          </div>

          {/* Chevron */}
          <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>

        {open && renderDropdown()}
      </div>
    </div>
  );

  // ─── Dropdown panel ───
  function renderDropdown() {
    const _um = t.userMenu as Record<string, string>;

    return createPortal(
      <div
        ref={dropdownRef}
        dir={isRtl ? "rtl" : "ltr"}
        className="fixed z-[60] rounded-xl border border-white/[0.06] overflow-hidden"
        style={{
          // Brand v2 popover: translucent pure-neutral + macOS vibrancy.
          backgroundColor: "rgba(10, 10, 10, 0.86)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
          top: dropPos.top,
          left: dropPos.left,
          width: dropPos.width,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
          animation: "ws-drop-in 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
          transformOrigin: "top center",
        }}
      >
        <style>{`
          @keyframes ws-drop-in {
            0% { opacity: 0; transform: translateY(-8px) scaleY(0.92); }
            40% { opacity: 1; }
            100% { opacity: 1; transform: translateY(0) scaleY(1); }
          }
        `}</style>

        {/* Current workspace header */}
        {activeItem && (
          <div className={`flex items-center gap-3 px-4 py-3 bg-[var(--cc-accent-500)]/[0.06] border-b border-slate-700/40 ${isRtl ? "flex-row-reverse" : ""}`}>
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${activeItem.gradient} border-2 border-[var(--cc-accent-500)]/30`}>
              <activeItem.icon className="h-4 w-4 text-white" />
            </div>
            <div className={`flex-1 min-w-0 ${isRtl ? "text-end" : ""}`}>
              <div className="text-sm font-semibold text-slate-100">{activeItem.name}</div>
              <div className="text-[10px] text-[var(--cc-accent-400)] font-medium mt-0.5">Super Admin</div>
            </div>
            <Check className="h-4 w-4 text-[var(--cc-accent-400)] shrink-0" />
          </div>
        )}

        {/* Sections */}
        <div className="max-h-[60vh] overflow-y-auto">
          {orderedSections.map((section) => (
            <div key={section.label} className="border-b border-slate-700/20 last:border-b-0 py-1.5">
              <div className={`px-4 py-1 text-[9px] font-semibold uppercase tracking-widest text-slate-600 ${isRtl ? "text-end" : ""}`}>
                {section.label}
              </div>
              {section.items.map((item) => {
                const isActive = item.id === activeItemId;
                const Row = item.external ? "a" : "button";
                return (
                  <Row
                    key={item.id}
                    dir={isRtl ? "rtl" : "ltr"}
                    draggable={!item.disabled}
                    onDragStart={() => setDraggedItem(item.id)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => moveItem(section.label, item.id)}
                    onDragEnd={() => setDraggedItem(null)}
                    type={item.external ? undefined : "button"}
                    href={item.external ? item.href : undefined}
                    disabled={item.external ? undefined : item.disabled}
                    aria-current={isActive ? "page" : undefined}
                    onClick={() => {
                      if (item.external) {
                        setOpen(false);
                        return;
                      }
                      if (!item.disabled) {
                        setActiveWs(item.id);
                        setOpen(false);
                        if (item.href) router.push(item.href);
                      }
                    }}
                    className={`flex w-full flex-row items-center gap-2.5 px-4 py-2 transition-colors cursor-pointer ${
                      isActive ? "bg-[var(--cc-accent-500)]/[0.06]" : "hover:bg-white/[0.03]"
                    } ${item.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <span
                      aria-label={lang === "he" ? "ידית גרירה לסידור" : "Drag to reorder"}
                      title={lang === "he" ? "גרור לסידור" : "Drag to reorder"}
                      className="flex h-7 w-5 shrink-0 cursor-grab items-center justify-center rounded border border-[var(--cc-fg-muted)]/35 bg-white/[0.04] text-[var(--cc-fg-muted)] transition-colors hover:border-[var(--cc-accent-400)]/60 hover:bg-[var(--cc-accent-500)]/[0.12] hover:text-[var(--cc-accent-300)] active:cursor-grabbing"
                    >
                      <GripVertical className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${item.gradient}`}>
                      <item.icon className="h-4 w-4 text-white" />
                    </div>
                    <div className={`flex-1 min-w-0 ${isRtl ? "text-end" : "text-start"}`}>
                      <div className="text-[13px] font-medium text-slate-200 truncate">{item.name}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{item.desc}</div>
                    </div>
                    {item.badge && (
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${item.badge.class} shrink-0`}>
                        {item.badge.label}
                      </span>
                    )}
                    {item.notif && (
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
                    )}
                    {isActive && !item.badge && (
                      <Check className="h-3.5 w-3.5 text-[var(--cc-accent-400)] shrink-0" />
                    )}
                  </Row>
                );
              })}
            </div>
          ))}
        </div>

        {/* User + logout */}
        <div className={`flex items-center gap-2 border-t border-slate-700/40 px-3 py-2 ${isRtl ? "flex-row-reverse" : ""}`}>
          <div className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[var(--cc-accent-500)] to-teal-500">
            <span className="text-[9px] font-semibold text-white">{userInitial}</span>
            <span className="absolute -bottom-px -end-px h-2 w-2 rounded-full border-[1.5px] border-slate-900 bg-emerald-500" />
          </div>
          <div className={`flex-1 min-w-0 ${isRtl ? "text-end" : "text-start"}`}>
            <div className="truncate text-[11px] font-medium text-slate-300">{userName}</div>
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); signOut(); }}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-600 hover:text-red-400 hover:bg-red-500/[0.08] transition-colors cursor-pointer"
            title={(t.userMenu as Record<string, string>).signOut || "Log Out"}
          >
            <LogOut className="h-3 w-3" />
          </button>
        </div>
      </div>,
      document.body
    );
  }
}
